import React, { useState, useEffect } from "react";
import {
    Modal,
    Row,
    Col,
    Card,
    Table,
    Typography,
    Tag,
    Space,
    Select,
    InputNumber,
    Checkbox,
    Button,
    Input,
    message,
} from "antd";

const { Text } = Typography;

const OrderDetailsModal = ({ visible, onClose, orderDetails }) => {
    const [selectedRows, setSelectedRows] = useState([]);
    const [items, setItems] = useState([]);
    const [shippingOptions, setShippingOptions] = useState([]);
    const [selectedShipping, setSelectedShipping] = useState(null);
    const [freightAmount, setFreightAmount] = useState(0);
    const [insurance, setInsurance] = useState(0);
    const [selectedSupplierText, setSelectedSupplierText] = useState("");
    const [endUserPO, setEndUserPO] = useState("");

    useEffect(() => {
        if (orderDetails) {
            const updatedItems = orderDetails.OrderItems.map((item) => ({
                ...item,
                OrderQuantity: item.Quantity,
                SelectedSupplierText: item.Suppliers?.[0]?.SupplierName || "",
            }));
            setItems(updatedItems);
            const defaultSupplier = updatedItems[0]?.SelectedSupplierText;
            if (defaultSupplier) {
                setSelectedSupplierText(defaultSupplier);
                fetchShippingQuote(defaultSupplier, updatedItems);
            }
        }
    }, [orderDetails]);

    const buildPayload = () => ({
        OrderId: orderDetails.ORDER_ID,
        PurchaseDate: orderDetails.CREATED_DATE,
        BuyerEmail: orderDetails.Email,
        BuyerName: `${orderDetails.FirstName} ${orderDetails.LastName}`,
        BuyerPhoneNumber: orderDetails.Phone,
        Supplier: selectedSupplierText,
        OrderStatus: orderDetails.ORDER_STATE,
        TrackingId: "",
        SupplierOrderId: "",
        PONumber: endUserPO,
        RecipientName: `${orderDetails.FirstName} ${orderDetails.LastName}`,
        ShipAddress1: orderDetails.Street1,
        ShipAddress2: orderDetails.Street2,
        ShipCity: orderDetails.City,
        ShipState: orderDetails.State,
        ShipPostalCode: orderDetails.ZipCode,
        ShipCountry: orderDetails.Country,
        ShipPhoneNumber: orderDetails.ShippingPhone,
        AddressType: "Shipping",
        Currency: "CAD",
        ShippingPrice: freightAmount,
        ShippingTax: insurance,
        ShipServiceLevel: selectedShipping,
        IsBuyerRequestedCancellation: "false",
        BuyerRequestedCancelReason: "",
        Items: items
            .filter((x) => selectedRows.includes(x.Sku))
            .map((item) => ({
                OrderItemId: `${orderDetails.ORDER_ID}-${item.Sku}`,
                OrderId: orderDetails.ORDER_ID,
                SKU: item.Sku,
                Earning: "",
                ProductName: item.Title,
                QuantityPurchased: item.Quantity,
                QuantityOrdered: item.OrderQuantity,
                ItemPrice: item.Price,
                ItemTax: 0,
            })),
        EndUserPO: endUserPO,
    });

    const fetchShippingQuote = async (supplierName, itemsData) => {
        try {
            const payload = buildPayload();
            const response = await fetch("/api/Supplier/GetShippingQuote", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Selected-Supplier": supplierName,
                },
                body: JSON.stringify(payload),
            });
            const data = await response.json();
            const options =
                data?.Data?.Items?.map((x) => ({
                    value: x.ServiceLevelCode,
                    label: x.ServiceLevelDescription,
                    freight: x.FreightAmount,
                })) || [];
            setShippingOptions(options);
            setInsurance(data?.Data?.Insurance || 0);
            if (options.length > 0) {
                setSelectedShipping(options[0].value);
                setFreightAmount(options[0].freight);
            }
        } catch (err) {
            console.error(err);
            message.error("Failed to fetch shipping quote");
        }
    };

    const handleQtyChange = (index, value) => {
        const newItems = [...items];
        newItems[index].OrderQuantity = value;
        setItems(newItems);
    };

    const handleSupplierChange = (index, supplierName) => {
        const newItems = [...items];
        newItems[index].SelectedSupplierText = supplierName;
        setItems(newItems);
        setSelectedSupplierText(supplierName);
        fetchShippingQuote(supplierName, newItems);
    };

    const handleShippingChange = (value) => {
        setSelectedShipping(value);
        const option = shippingOptions.find((x) => x.value === value);
        if (option) setFreightAmount(option.freight);
    };

    const handleAccept = async () => {
        try {
            const payload = buildPayload();
            const response = await fetch("/api/Supplier/GetShippingQuote", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Selected-Supplier": selectedSupplierText,
                },
                body: JSON.stringify(payload),
            });
            const data = await response.json();
            message.success("Shipping quote sent successfully!");
            console.log("Payload:", payload);
            console.log("Response:", data);
        } catch (err) {
            console.error(err);
            message.error("Failed to send shipping quote");
        }
    };

    const orderItemsColumns = [
        { title: "SKU", dataIndex: "Sku", key: "Sku" },
        { title: "Title", dataIndex: "Title", key: "Title" },
        { title: "Qty", dataIndex: "Quantity", key: "Quantity" },
        {
            title: "Order Qty",
            key: "OrderQuantity",
            render: (_, record, index) => (
                <InputNumber
                    min={0}
                    value={record.OrderQuantity || 0}
                    onChange={(val) => handleQtyChange(index, val)}
                    style={{ width: 80 }}
                />
            ),
        },
        {
            title: "Price",
            dataIndex: "Price",
            key: "Price",
            render: (val) => `$${val?.toFixed(2)}`,
        },
        {
            title: "Supplier",
            key: "Supplier",
            render: (_, record, index) => (
                <Select
                    style={{ width: "100%" }}
                    value={record.SelectedSupplierText}
                    onChange={(val) => handleSupplierChange(index, val)}
                    options={record.Suppliers?.map((s) => ({
                        value: s.SupplierName,
                        label: `${s.SupplierName} (Qty:${s.Availability} $${s.Price})`,
                    }))}
                />
            ),
        },
        {
            title: "Select",
            key: "Select",
            render: (_, record) => (
                <Checkbox
                    checked={selectedRows.includes(record.Sku)}
                    onChange={(e) => {
                        if (e.target.checked)
                            setSelectedRows([...selectedRows, record.Sku]);
                        else setSelectedRows(selectedRows.filter((x) => x !== record.Sku));
                    }}
                />
            ),
        },
    ];

    if (!orderDetails) return null;

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            footer={null}
            width={1200}
            title={
                <Space>
                    <Text strong style={{ fontSize: 16 }}>
                        Order #{orderDetails.ORDER_ID}
                    </Text>
                    <Tag color="blue">{orderDetails.ORDER_STATE}</Tag>
                </Space>
            }
            bodyStyle={{ padding: 20 }}
        >
            {/* Top Info */}
            <Row gutter={16}>
                <Col xs={24} sm={12}>
                    <Card
                        size="small"
                        style={{ padding: 16, borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}
                    >
                        <h4 style={{ marginBottom: 12 }}>Order Info</h4>
                        <div><b>Total:</b> ${orderDetails.TotalAmount.toFixed(2)}</div>
                        <div><b>Order Date:</b> {new Date(orderDetails.CREATED_DATE).toLocaleString()}</div>
                        <div><b>Shipping Deadline:</b> {new Date(orderDetails.ShippingDeadline).toLocaleString()}</div>
                    </Card>
                </Col>

                <Col xs={24} sm={12}>
                    <Card
                        size="small"
                        style={{ padding: 16, borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}
                    >
                        <h4 style={{ marginBottom: 12 }}>Shipping & PO</h4>
                        <Input
                            placeholder="End User PO"
                            value={endUserPO}
                            onChange={(e) => setEndUserPO(e.target.value)}
                            style={{ marginBottom: 12 }}
                        />
                        <Select
                            placeholder="Shipping Service"
                            value={selectedShipping}
                            onChange={handleShippingChange}
                            options={shippingOptions}
                            style={{ width: "100%" }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Customer & Address */}
            <Row gutter={16} style={{ marginTop: 16 }}>
                <Col xs={24} sm={12}>
                    <Card
                        size="small"
                        title="Customer"
                        style={{ borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}
                    >
                        <div><b>Name:</b> {orderDetails.FirstName} {orderDetails.LastName}</div>
                        <div><b>Email:</b> {orderDetails.Email || "-"}</div>
                        <div><b>Phone:</b> {orderDetails.Phone || "-"}</div>
                    </Card>
                </Col>

                <Col xs={24} sm={12}>
                    <Card
                        size="small"
                        title="Shipping Address"
                        style={{ borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}
                    >
                        <div><b>Address:</b> {orderDetails.Street1 || "-"} {orderDetails.Street2 || ""}</div>
                        <div><b>City:</b> {orderDetails.City || "-"}</div>
                        <div><b>State:</b> {orderDetails.State || "-"}</div>
                        <div><b>Zip Code:</b> {orderDetails.ZipCode || "-"}</div>
                        <div><b>Country:</b> {orderDetails.Country || "-"}</div>
                        <div><b>Phone:</b> {orderDetails.ShippingPhone || "-"}</div>
                    </Card>
                </Col>
            </Row>

            {/* Items Table */}
            <Card
                size="small"
                title={`Items (${orderDetails.ItemCount})`}
                style={{ marginTop: 16, borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}
            >
                <Table
                    dataSource={items}
                    columns={orderItemsColumns}
                    rowKey={(x) => x.Sku + x.ProductId}
                    pagination={false}
                    size="small"
                />
            </Card>

            {/* Action Buttons */}
            <Row justify="end" style={{ marginTop: 24 }}>
                <Col>
                    <Button type="primary" onClick={handleAccept}>
                        Accept
                    </Button>
                    <Button danger style={{ marginLeft: 12 }} onClick={onClose}>
                        Reject
                    </Button>
                </Col>
            </Row>
        </Modal>
    );
};

export default OrderDetailsModal;